import { extractStateValue } from "@src/components/DefuseSDK/features/common/statusUtils"
import type { TrackedSwapRef } from "@src/machines/swapTrackerMachine"

export {
  type StatusActorSnapshot,
  extractStateValue,
} from "@src/components/DefuseSDK/features/common/statusUtils"

export const SWAP_STAGES = ["pending", "executing", "complete"] as const
export type SwapStage = (typeof SWAP_STAGES)[number]

export const SWAP_STAGE_LABELS: Record<SwapStage, string> = {
  pending: "Finding best price",
  executing: "Executing trade",
  complete: "Trade complete",
}

export function getStageFromState(stateValue: string): SwapStage {
  switch (stateValue) {
    case "pending":
    case "checking":
    case "intent_settling":
    case "SubmittingTxHash":
      return "pending"
    case "settled":
    case "waitingForBridge":
    case "retryDelay":
    case "waiting":
    case "polling":
      return "executing"
    case "success":
    case "error":
    case "not_valid":
      return "complete"
    default:
      return "pending"
  }
}

export function is1csError(status: string | null | undefined): boolean {
  return (
    status === "FAILED" ||
    status === "REFUNDED" ||
    status === "FAILED_EXECUTION"
  )
}

export function is1csSuccess(status: string | null | undefined): boolean {
  return status === "SUCCESS"
}

export function isIntentError(stateValue: string): boolean {
  return stateValue === "error" || stateValue === "not_valid"
}

export function isIntentSuccess(stateValue: string): boolean {
  return stateValue === "success"
}

/**
 * Checks whether a tracked swap has reached a terminal state (success or error).
 * Encapsulates the snapshot type narrowing for both 1-Click and intent-based swaps.
 */
export function isSwapComplete(swap: TrackedSwapRef): boolean {
  const snapshot = swap.actorRef.getSnapshot() as {
    value: string | object
    context: { status?: string | null }
  }
  const stateValue = extractStateValue(snapshot.value)
  const contextStatus = snapshot.context?.status
  return swap.is1cs
    ? is1csSuccess(contextStatus) || is1csError(contextStatus)
    : isIntentSuccess(stateValue) || isIntentError(stateValue)
}
